import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (err) {
      // Backend always returns 200 generic — show generic on any error too
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex flex-col">
      <header className="bg-[#0f1b3d] text-white px-6 py-5">
        <Logo size="sm" variant="light" linkTo="/" />
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md shadow-lg border-0" data-testid="forgot-password-card">
          {!submitted ? (
            <>
              <CardHeader className="space-y-1 pb-6">
                <CardTitle className="text-2xl font-bold text-slate-900 font-['Public_Sans']">
                  Forgot password?
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Enter your email and we'll send you reset instructions.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-14 rounded-xl bg-slate-50 border-slate-200"
                      data-testid="forgot-password-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-14 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white font-semibold text-lg"
                    disabled={loading}
                    data-testid="forgot-password-submit-btn"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Sending...</>
                    ) : (
                      'Send reset link'
                    )}
                  </Button>
                </form>
                <p className="mt-6 text-center text-slate-600">
                  <Link to="/login" className="text-[#DA291C] font-semibold hover:underline inline-flex items-center" data-testid="back-to-login-link">
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back to sign in
                  </Link>
                </p>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="space-y-1 pb-6 text-center">
                <div className="mx-auto h-16 w-16 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                  <Mail className="h-8 w-8 text-emerald-600" />
                </div>
                <CardTitle className="text-2xl font-bold text-slate-900 font-['Public_Sans']">
                  Check your email
                </CardTitle>
                <CardDescription className="text-slate-500" data-testid="forgot-password-success-message">
                  If an account exists for that email, we'll send reset instructions. The link expires in 30 minutes.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/login">
                  <Button
                    variant="outline"
                    className="w-full h-12 rounded-full border-2 border-[#0f1b3d] text-[#0f1b3d] font-medium"
                    data-testid="forgot-password-back-to-login-btn"
                  >
                    Back to sign in
                  </Button>
                </Link>
              </CardContent>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}
